import { SearchIcon, TableIcon } from 'lucide-react'
import { ReferenceFieldData, ReferenceRecordProperty, TextRecordProperty } from '@indigohive/cogfy-types'
import { useTranslation } from 'react-i18next'
import { useCollection, useCollectionRecords } from '../../../../hooks'
import { DEFAULT_FIELD_WIDTH } from '../../../../pages/CollectionPageV2/components'
import { FieldOverlayProps } from '../../FieldOverlay'
import { useDebounce } from 'use-debounce'
import { useMemo, useState } from 'react'
import { RecordsMenu } from './components'

const MIN_WIDTH = 200
const PAGE_SIZE = 200

export function ReferenceOverlay (props: FieldOverlayProps) {
  const { field, viewField, record, controller } = props

  const { t } = useTranslation()
  const [search, setQuery] = useState<string>('')
  const [debouncedSearch] = useDebounce(search, 300)

  const recordProperties = record.properties[field.id] as ReferenceRecordProperty | undefined
  const recordReferences = recordProperties?.reference?.value ?? []

  const fieldData = field.data as ReferenceFieldData | undefined
  const referencedCollectionId = fieldData?.reference?.collectionId
  const limit = fieldData?.reference?.limit

  const getReferencedCollection = useCollection(referencedCollectionId)
  const getCollectionRecords = useCollectionRecords({ collectionId: referencedCollectionId, onlyTitles: true, pageSize: PAGE_SIZE, search: debouncedSearch })

  const referencedCollection = getReferencedCollection.data
  const collectionRecords = getCollectionRecords.data?.data ?? []

  const linkedRecords = useMemo(() => {
    if (!referencedCollection?.titleFieldId) return []

    const referencedRecordsById = Object.fromEntries(collectionRecords.map(record => [record.id, record]))

    const linkedRecords = recordReferences
      .map(recordReference => {
        const referencedRecord = referencedRecordsById[recordReference.referencedRecordId]
        const referencedRecordProperties = referencedRecord?.properties[referencedCollection.titleFieldId!] as TextRecordProperty | undefined
        const title = referencedRecordProperties?.text?.value

        return {
          id: recordReference.referencedRecordId,
          recordReferenceId: recordReference.id,
          title
        }
      })
      .filter(linked => linked !== null && collectionRecords.some(record => record.id === linked.id))

    return linkedRecords
  }, [collectionRecords, recordReferences, referencedCollection?.titleFieldId])

  const unlikedRecords = useMemo(() => {
    if (!referencedCollection?.titleFieldId) return []

    const unlikedRecords = collectionRecords
      .filter(record => {
        const isLinked = linkedRecords.find(linkedRecord => linkedRecord.id === record.id)
        const isDeleted = recordReferences.find(recordReference => recordReference.referencedRecordId === record.id)

        return !isLinked && !isDeleted
      })
      .map(record => ({
        id: record.id,
        title: (record.properties[referencedCollection.titleFieldId!] as TextRecordProperty)?.text?.value
      }))

    return unlikedRecords
  }, [collectionRecords, recordReferences, referencedCollection?.titleFieldId])

  const width = Math.max(viewField.config?.ui?.width ?? DEFAULT_FIELD_WIDTH, MIN_WIDTH)

  return (
    <div
      className="menu-sm bg-base-100 rounded-btn border shadow-lg over max-w-full"
      style={{ width }}
    >
      <div className="px-3 pt-4 pb-2">
        <label className="input input-xs input-bordered flex items-center gap-2 w-full">
          <SearchIcon size={14} />
          <input
            autoFocus
            type="input"
            className="w-full h-min"
            placeholder={t('Search')}
            value={search}
            onChange={event => setQuery(event.target.value)}
          />
        </label>
      </div>

      <ul
        className="overflow-y-auto overflow-x-hidden w-full max-h-96 px-2"
        style={{ width, scrollbarWidth: 'thin' }}
      >
        {!referencedCollection && !getReferencedCollection.isLoading && <li className="menu-title">{t('Collection not found')}</li>}
        {getReferencedCollection.isLoading && <progress className="progress w-full" />}
        {referencedCollection && (
          <>
            <div className="flex gap-1 items-center pl-2 pt-2">
              <span>{t('In')}</span>
              <TableIcon size={14} />
              <span>
                {referencedCollection?.name}
              </span>
            </div>

            {!referencedCollection?.titleFieldId && (
              <li className="menu-title">
                {t('No title field')}
              </li>
            )}
            {referencedCollection?.titleFieldId && (
              <RecordsMenu
                fieldId={field.id}
                linkedRecords={linkedRecords}
                recordId={record.id}
                unlinkedRecords={unlikedRecords}
                limit={limit}
                controller={controller}
              />
            )}
          </>
        )}
      </ul>
    </div>
  )
}
